import React, { useState, useEffect } from 'react';
import { Activity, DollarSign, Zap, TrendingUp, AlertCircle, RefreshCw, Calendar, BarChart3, PieChart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../config/API_URL';

// Toast Component (reused from your existing code)
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const styles = {
    container: {
      position: 'fixed',
      top: '2rem',
      right: '2rem',
      background: type === 'success' ? 'rgba(16, 185, 129, 0.95)' : 'rgba(239, 68, 68, 0.95)',
      backdropFilter: 'blur(10px)',
      borderRadius: '12px',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
      padding: '1rem 1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      zIndex: 10000,
      border: `1px solid ${type === 'success' ? '#10b981' : '#ef4444'}`,
      minWidth: '300px',
      color: 'white',
    },
  };

  return (
    <motion.div
      initial={{ x: 400, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 400, opacity: 0 }}
      style={styles.container}
    >
      {type === 'success' ? '✓' : '✕'}
      <span>{message}</span>
    </motion.div>
  );
};

// Stats Card Component
const StatsCard = ({ title, value, subtitle, icon: Icon, color, trend }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        background: 'rgba(20, 20, 35, 0.6)',
        backdropFilter: 'blur(20px)',
        borderRadius: '16px',
        padding: '1.5rem',
        border: `1px solid ${color}30`,
        boxShadow: `0 4px 30px ${color}20`,
        position: 'relative',
        overflow: 'hidden',
      }}
      whileHover={{
        scale: 1.02,
        boxShadow: `0 8px 40px ${color}30`,
      }}
    >
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-10%',
        width: '150px',
        height: '150px',
        background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
        borderRadius: '50%',
      }} />
      
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '0.75rem',
        }}>
          <span style={{
            fontSize: '0.9rem',
            color: '#a0a0c0',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '1px',
            fontFamily: "'Rajdhani', sans-serif",
          }}>
            {title}
          </span>
          <Icon size={24} style={{ color: color }} />
        </div>
        
        <div style={{
          fontSize: '2.5rem',
          fontWeight: 900,
          fontFamily: "'Orbitron', monospace",
          background: `linear-gradient(135deg, ${color}, ${color}80)`,
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '0.5rem',
        }}>
          {value}
        </div>
        
        {subtitle && (
          <div style={{
            fontSize: '0.85rem',
            color: '#a0a0c0',
            fontWeight: 500,
          }}>
            {subtitle}
          </div>
        )}
        
        {trend && (
          <div style={{
            fontSize: '0.85rem',
            color: trend > 0 ? '#00ff88' : '#ff3366',
            fontWeight: 600,
            marginTop: '0.5rem',
          }}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% from yesterday
          </div>
        )}
      </div>
    </motion.div>
  );
};

// Model Usage Card
const ModelUsageCard = ({ model, tokens, cost, percentage }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      style={{
        background: 'rgba(20, 20, 35, 0.4)',
        borderRadius: '12px',
        padding: '1rem',
        marginBottom: '0.75rem',
        border: '1px solid rgba(0, 255, 245, 0.2)',
      }}
      whileHover={{
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.5rem',
      }}>
        <span style={{
          fontSize: '1rem',
          fontWeight: 700,
          color: '#00fff5',
          fontFamily: "'Orbitron', monospace",
        }}>
          {model}
        </span>
        <span style={{
          fontSize: '0.9rem',
          color: '#8b5cf6',
          fontWeight: 600,
        }}>
          ${cost.toFixed(2)}
        </span>
      </div>
      
      <div style={{
        fontSize: '0.85rem',
        color: '#a0a0c0',
        marginBottom: '0.5rem',
        fontFamily: "'Rajdhani', sans-serif",
      }}>
        {tokens.toLocaleString()} tokens
      </div>
      
      <div style={{
        width: '100%',
        height: '6px',
        background: 'rgba(0, 255, 245, 0.2)',
        borderRadius: '3px',
        overflow: 'hidden',
      }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{
            height: '100%',
            background: 'linear-gradient(90deg, #00fff5, #8b5cf6)',
            borderRadius: '3px',
          }}
        />
      </div>
    </motion.div>
  );
};

// Time Range Selector
const TimeRangeSelector = ({ selectedRange, onRangeChange }) => {
  const ranges = [
    { value: '7d', label: '7 Days' },
    { value: '30d', label: '30 Days' },
    { value: '90d', label: '90 Days' },
  ];

  return (
    <div style={{
      display: 'flex',
      gap: '0.5rem',
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '12px',
      padding: '0.5rem',
      border: '1px solid rgba(0, 255, 245, 0.2)',
    }}>
      {ranges.map((range) => (
        <motion.button
          key={range.value}
          onClick={() => onRangeChange(range.value)}
          style={{
            padding: '0.5rem 1rem',
            background: selectedRange === range.value 
              ? 'linear-gradient(135deg, rgba(0, 255, 245, 0.2), rgba(139, 92, 246, 0.2))' 
              : 'transparent',
            border: selectedRange === range.value 
              ? '1px solid #00fff5' 
              : '1px solid transparent',
            borderRadius: '8px',
            color: selectedRange === range.value ? '#00fff5' : '#a0a0c0',
            fontSize: '0.9rem',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: "'Rajdhani', sans-serif",
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
          whileHover={{
            scale: 1.05,
            backgroundColor: selectedRange === range.value 
              ? undefined 
              : 'rgba(255, 255, 255, 0.05)',
          }}
          whileTap={{ scale: 0.95 }}
        >
          {range.label}
        </motion.button>
      ))}
    </div>
  );
};

// Main AI Management Page Component
const AIManagementPage = () => {
  const [loading, setLoading] = useState(true);
  const [hasApiAccess, setHasApiAccess] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');

  useEffect(() => {
    loadOpenAIMetrics();
    const interval = setInterval(loadOpenAIMetrics, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [timeRange]);

  const getDateRange = () => {
    const end = new Date();
    let start = new Date();
    
    switch(timeRange) {
      case '7d':
        start.setDate(start.getDate() - 7);
        break;
      case '30d':
        start.setDate(start.getDate() - 30);
        break;
      case '90d':
        start.setDate(start.getDate() - 90);
        break;
      default:
        start.setDate(start.getDate() - 30);
    }
    
    return {
      start_date: start.toISOString().split('T')[0],
      end_date: end.toISOString().split('T')[0]
    };
  };

  const loadOpenAIMetrics = async () => {
    try {
      // Check API access
      const checkResponse = await fetch(`${API_URL}/admin/openai/check-access`, {
        credentials: 'include'
      });
      
      const checkData = await checkResponse.json();
      
      if (!checkResponse.ok || !checkData.has_access) {
        setHasApiAccess(false);
        setLoading(false);
        return;
      }
      
      setHasApiAccess(true);
      
      // Get date range
      const { start_date, end_date } = getDateRange();
      
      // Load billing usage data
      const usageResponse = await fetch(
        `${API_URL}/admin/openai/billing/usage?start_date=${start_date}&end_date=${end_date}`, 
        { credentials: 'include' }
      );
      
      if (usageResponse.ok) {
        const usage = await usageResponse.json();
        setUsageData(usage);
      }
      
      // Load subscription data
      const subResponse = await fetch(`${API_URL}/admin/openai/subscription`, {
        credentials: 'include'
      });
      
      if (subResponse.ok) {
        const sub = await subResponse.json();
        setSubscriptionData(sub.subscription);
      }
      
    } catch (error) {
      console.error('Failed to load OpenAI metrics:', error);
      setToastMessage('Failed to load AI metrics');
      setToastType('error');
      setShowToast(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setLoading(true);
    loadOpenAIMetrics();
  };

  const calculateCost = (tokens, model = 'whisper-1') => {
    // OpenAI pricing (as of Jan 2025) per 1M tokens
    const pricing = {
      'whisper-1': 0.006, // per minute of audio
      'gpt-4': 30.00, // input per 1M
      'gpt-4-turbo': 10.00,
      'gpt-3.5-turbo': 0.50
    };
    
    return (tokens / 1_000_000) * (pricing[model] || 1.00);
  };

  const styles = {
    container: {
      padding: '0',
    },
    header: {
      marginBottom: '2rem',
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      background: 'linear-gradient(135deg, #00fff5, #8b5cf6)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '0.5rem',
      letterSpacing: '2px',
    },
    subtitle: {
      fontSize: '1.1rem',
      color: '#a0a0c0',
      fontFamily: "'Rajdhani', sans-serif",
      fontWeight: 500,
    },
    controlsBar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      flexWrap: 'wrap',
      gap: '1rem',
    },
    statsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: '1.5rem',
      marginBottom: '2rem',
    },
    section: {
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      padding: '1.5rem',
      border: '1px solid rgba(0, 255, 245, 0.2)',
      boxShadow: '0 4px 30px rgba(0, 255, 245, 0.1)',
      marginBottom: '2rem',
    },
    sectionTitle: {
      fontSize: '1.5rem',
      fontWeight: 700,
      fontFamily: "'Orbitron', monospace",
      color: '#00fff5',
      marginBottom: '1.5rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
    },
    emptyState: {
      textAlign: 'center',
      padding: '4rem 2rem',
      color: '#a0a0c0',
    },
    warningBox: {
      background: 'rgba(255, 170, 0, 0.1)',
      border: '1px solid rgba(255, 170, 0, 0.3)',
      borderRadius: '12px',
      padding: '1.5rem',
      display: 'flex',
      alignItems: 'start',
      gap: '1rem',
      color: '#ffaa00',
    },
    button: {
      padding: '0.75rem 1.5rem',
      background: 'rgba(20, 20, 35, 0.6)',
      backdropFilter: 'blur(20px)',
      border: '1px solid rgba(0, 255, 245, 0.3)',
      borderRadius: '12px',
      color: '#00fff5',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontFamily: "'Rajdhani', sans-serif",
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={{ ...styles.emptyState, padding: '6rem 2rem' }}>
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          >
            <RefreshCw size={48} style={{ color: '#8b5cf6' }} />
          </motion.div>
          <div style={{ fontSize: '1.2rem', marginTop: '1rem', fontFamily: "'Rajdhani', sans-serif" }}>
            Loading AI metrics...
          </div>
        </div>
      </div>
    );
  }

  if (hasApiAccess === false) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            style={styles.title}
          >
            AI MANAGEMENT
          </motion.h1>
          <p style={styles.subtitle}>
            Monitor OpenAI API usage, costs, and performance
          </p>
        </div>

        <div style={styles.warningBox}>
          <AlertCircle size={24} style={{ flexShrink: 0, marginTop: '0.25rem' }} />
          <div>
            <h3 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: '0.5rem', fontFamily: "'Orbitron', monospace" }}>
              OpenAI API Key Required
            </h3>
            <p style={{ fontSize: '1rem', lineHeight: 1.6, color: '#e0e0ff' }}>
              To access AI usage and cost metrics, you need to configure your OpenAI API key:
            </p>
            <ol style={{ paddingLeft: '1.5rem', lineHeight: 1.8, color: '#e0e0ff', marginTop: '1rem' }}>
              <li>Add <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px' }}>OPENAI_API_KEY</code> to your <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px' }}>.env</code> file</li>
              <li>Restart your Flask server</li>
              <li>Refresh this page</li>
            </ol>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <AnimatePresence>
        {showToast && (
          <Toast
            message={toastMessage}
            type={toastType}
            onClose={() => setShowToast(false)}
          />
        )}
      </AnimatePresence>

      <div style={styles.header}>
        <motion.h1
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={styles.title}
        >
          AI MANAGEMENT
        </motion.h1>
        <p style={styles.subtitle}>
          Real-time OpenAI API usage monitoring and cost tracking
        </p>
      </div>

      <div style={styles.controlsBar}>
        <TimeRangeSelector selectedRange={timeRange} onRangeChange={setTimeRange} />
        
        <motion.button
          style={styles.button}
          onClick={handleRefresh}
          whileHover={{
            scale: 1.05,
            boxShadow: '0 0 20px rgba(0, 255, 245, 0.4)',
          }}
          whileTap={{ scale: 0.95 }}
        >
          <RefreshCw size={18} />
          Refresh
        </motion.button>
      </div>

      {usageData && (
        <div style={styles.statsGrid}>
          <StatsCard
            title="Total Tokens"
            value={usageData.total_tokens?.toLocaleString() || '0'}
            subtitle={`${timeRange.toUpperCase()} usage`}
            icon={Zap}
            color="#00fff5"
          />
          <StatsCard
            title="Total Cost"
            value={`$${usageData.total_cost?.toFixed(2) || '0.00'}`}
            subtitle={`${timeRange.toUpperCase()} spending`}
            icon={DollarSign}
            color="#8b5cf6"
          />
          <StatsCard
            title="Models Used"
            value={usageData.models?.length || '0'}
            subtitle="Active models"
            icon={Activity}
            color="#00ff88"
          />
          <StatsCard
            title="Daily Average"
            value={usageData.daily_usage?.length > 0 
              ? `${Math.round(usageData.total_tokens / usageData.daily_usage.length).toLocaleString()}`
              : '0'}
            subtitle="Tokens per day"
            icon={TrendingUp}
            color="#ffaa00"
          />
        </div>
      )}

      {subscriptionData && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <Calendar size={24} />
            Subscription Info
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
          }}>
            <div style={{
              background: 'rgba(0, 255, 245, 0.1)',
              padding: '1rem',
              borderRadius: '12px',
              border: '1px solid rgba(0, 255, 245, 0.3)',
            }}>
              <div style={{ 
                fontSize: '0.9rem', 
                color: '#a0a0c0', 
                marginBottom: '0.5rem',
                fontFamily: "'Rajdhani', sans-serif",
                textTransform: 'uppercase',
              }}>
                Plan Type
              </div>
              <div style={{ 
                fontSize: '1.5rem', 
                fontWeight: 900, 
                color: '#00fff5',
                fontFamily: "'Orbitron', monospace",
              }}>
                {subscriptionData.plan?.title || 'N/A'}
              </div>
            </div>
            
            {subscriptionData.hard_limit_usd && (
              <div style={{
                background: 'rgba(255, 170, 0, 0.1)',
                padding: '1rem',
                borderRadius: '12px',
                border: '1px solid rgba(255, 170, 0, 0.3)',
              }}>
                <div style={{ 
                  fontSize: '0.9rem', 
                  color: '#a0a0c0', 
                  marginBottom: '0.5rem',
                  fontFamily: "'Rajdhani', sans-serif",
                  textTransform: 'uppercase',
                }}>
                  Spending Limit
                </div>
                <div style={{ 
                  fontSize: '1.5rem', 
                  fontWeight: 900, 
                  color: '#ffaa00',
                  fontFamily: "'Orbitron', monospace",
                }}>
                  ${subscriptionData.hard_limit_usd}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          <PieChart size={24} />
          Model Usage Breakdown
        </div>
        
        {usageData?.models && usageData.models.length > 0 ? (
          usageData.models.map((model, index) => (
            <ModelUsageCard
              key={index}
              model={model.name}
              tokens={model.tokens}
              cost={calculateCost(model.tokens, model.name)}
              percentage={model.percentage}
            />
          ))
        ) : (
          <div style={{ 
            textAlign: 'center', 
            padding: '2rem', 
            color: '#a0a0c0',
            fontFamily: "'Rajdhani', sans-serif",
          }}>
            No model usage data available for this time range
          </div>
        )}
      </div>
    </div>
  );
};

export default AIManagementPage;